import { useState } from 'react';
import { Currency, type Money, type MoneyInput } from '~/graphql/graphql';
import type { ProductGroup } from '~/pages/rfq-view/itemGroups';
import { quantityInBundles } from '~/pages/rfq-view/bundleQuantity';
import { parsePriceInput } from '~/utils/moneyBounds';

export type ExistingQuoteItem = {
  id: string;
  quantityInPieces: number;
  piecesPerBundle?: number | null;
  offeredPickupPrice?: Money | null;
  offeredDeliveryPrice?: Money | null;
};

export type RowDraft = {
  quoteItemId: string | null;
  quantityInBundles: string;
  piecesPerBundle: number;
  pickupPrice: string;
  deliveryPrice: string;
  added?: true;
};

export type QuoteResponseDraft = {
  currency: Currency;
  leadTimeDays: string;
  sellingOrganizationFacilityId: string;
  wholeRequestPickupPrice: string;
  wholeRequestDeliveryPrice: string;
  rows: Map<string, RowDraft>;
};

export type QuoteResponseSubmission = {
  itemOverrides: ItemOverride[];
  currency: Currency;
  leadTimeDays: number;
  sellingOrganizationFacilityId: string | null;
  wholeRequestPickupPrice: MoneyInput | null;
  wholeRequestDeliveryPrice: MoneyInput | null;
  submit: boolean;
};

export type ItemOverride = {
  quoteItemId: string | null;
  platformProductLengthId: string;
  quantityInBundles: number;
  piecesPerBundle: number;
  offeredPickupPrice: MoneyInput | null;
  offeredDeliveryPrice: MoneyInput | null;
};

export function parsePrice(
  value: string,
  currency: Currency
): MoneyInput | null {
  const parsed = parsePriceInput(value);
  return parsed.valid && parsed.amount !== undefined
    ? { amount: parsed.amount, currency }
    : null;
}

function bundleCount(value: string): number {
  return parseInt(value, 10) || 0;
}

export function buildRowDrafts(
  groups: ProductGroup[],
  itemsByPlatformProductLengthId: Map<string, ExistingQuoteItem>
): Map<string, RowDraft> {
  const drafts = new Map<string, RowDraft>();
  for (const group of groups) {
    for (const row of group.rows) {
      const item = itemsByPlatformProductLengthId.get(
        row.platformProductLengthId
      );
      // The item's own pieces per bundle is preserved so a resubmit reconstructs the exact
      // original quantityInPieces rather than a value rounded through the group default.
      const piecesPerBundle = item?.piecesPerBundle ?? row.piecesPerBundle;
      drafts.set(row.platformProductLengthId, {
        quoteItemId: item?.id ?? null,
        quantityInBundles: item
          ? String(quantityInBundles(item.quantityInPieces, piecesPerBundle))
          : '',
        piecesPerBundle,
        pickupPrice: item?.offeredPickupPrice?.amount.toString() ?? '',
        deliveryPrice: item?.offeredDeliveryPrice?.amount.toString() ?? '',
      });
    }
  }
  return drafts;
}

function hasPriceText(value: string): boolean {
  return value.trim() !== '';
}

function hasPrice(value: string, currency: Currency): boolean {
  return parsePrice(value, currency) !== null;
}

function rowIdsWhere(
  draft: QuoteResponseDraft,
  isMatch: (row: RowDraft) => boolean
): Set<string> {
  return new Set(
    [...draft.rows.entries()]
      .filter(([, row]) => isMatch(row))
      .map(([platformProductLengthId]) => platformProductLengthId)
  );
}

export function rowsMissingQuantity(
  draft: QuoteResponseDraft,
  usesWholeRequestPrice: boolean
): Set<string> {
  return rowIdsWhere(
    draft,
    (row) =>
      bundleCount(row.quantityInBundles) <= 0 &&
      (usesWholeRequestPrice
        ? row.added === true && row.quoteItemId == null
        : hasPriceText(row.pickupPrice) || hasPriceText(row.deliveryPrice))
  );
}

export function rowsMissingPrice(
  draft: QuoteResponseDraft,
  usesWholeRequestPrice: boolean
): Set<string> {
  if (usesWholeRequestPrice) return new Set();
  return rowIdsWhere(
    draft,
    (row) =>
      bundleCount(row.quantityInBundles) > 0 &&
      !hasPrice(row.pickupPrice, draft.currency) &&
      !hasPrice(row.deliveryPrice, draft.currency)
  );
}

export function submissionErrorKey(
  draft: QuoteResponseDraft,
  usesWholeRequestPrice: boolean,
  { requiresShipFrom = true }: { requiresShipFrom?: boolean } = {}
): string | null {
  if (requiresShipFrom && !draft.sellingOrganizationFacilityId) {
    return 'rfq_view.ship_from_required_error';
  }

  const leadTimeDays = parseInt(draft.leadTimeDays, 10);
  if (!Number.isFinite(leadTimeDays) || leadTimeDays <= 0) {
    return 'rfq_view.lead_time_required_error';
  }

  if (rowsMissingQuantity(draft, usesWholeRequestPrice).size > 0) {
    return 'rfq_view.quantity_required_error';
  }

  const rowsWithQuantity = [...draft.rows.values()].filter(
    (row) => bundleCount(row.quantityInBundles) > 0
  );
  if (rowsWithQuantity.length === 0) {
    return 'rfq_view.price_required_error';
  }

  const priced = usesWholeRequestPrice
    ? hasPrice(draft.wholeRequestPickupPrice, draft.currency) ||
      hasPrice(draft.wholeRequestDeliveryPrice, draft.currency)
    : rowsMissingPrice(draft, usesWholeRequestPrice).size === 0;

  return priced ? null : 'rfq_view.price_required_error';
}

export function toItemOverrides(
  draft: QuoteResponseDraft,
  usesWholeRequestPrice: boolean
): ItemOverride[] {
  return [...draft.rows.entries()].map(
    ([platformProductLengthId, row]): ItemOverride => ({
      quoteItemId: row.quoteItemId,
      platformProductLengthId,
      quantityInBundles: bundleCount(row.quantityInBundles),
      piecesPerBundle: row.piecesPerBundle,
      offeredPickupPrice: usesWholeRequestPrice
        ? null
        : parsePrice(row.pickupPrice, draft.currency),
      offeredDeliveryPrice: usesWholeRequestPrice
        ? null
        : parsePrice(row.deliveryPrice, draft.currency),
    })
  );
}

export function quoteResponseSubmission(
  draft: QuoteResponseDraft,
  usesWholeRequestPrice: boolean,
  submit: boolean
): QuoteResponseSubmission {
  const wholeRequestMoney = (value: string) =>
    usesWholeRequestPrice ? parsePrice(value, draft.currency) : null;
  return {
    itemOverrides: toItemOverrides(draft, usesWholeRequestPrice),
    currency: draft.currency,
    leadTimeDays: parseInt(draft.leadTimeDays, 10),
    sellingOrganizationFacilityId: draft.sellingOrganizationFacilityId || null,
    wholeRequestPickupPrice: wholeRequestMoney(draft.wholeRequestPickupPrice),
    wholeRequestDeliveryPrice: wholeRequestMoney(
      draft.wholeRequestDeliveryPrice
    ),
    submit,
  };
}

export function withPatchedRow(
  draft: QuoteResponseDraft,
  platformProductLengthId: string,
  changes: Partial<RowDraft>
): QuoteResponseDraft {
  const existing = draft.rows.get(platformProductLengthId);
  if (!existing) return draft;
  const rows = new Map(draft.rows);
  rows.set(platformProductLengthId, { ...existing, ...changes });
  return { ...draft, rows };
}

export type QuoteResponseSeed = {
  groups: ProductGroup[];
  items: Map<string, ExistingQuoteItem>;
  currency: Currency | null;
  leadTimeDays: number | null;
  sellingOrganizationFacilityId: string | null;
  wholeRequestPickupPrice: { amount: number } | null;
  wholeRequestDeliveryPrice: { amount: number } | null;
};

export function seedQuoteResponseDraft(
  seed: QuoteResponseSeed,
  facilities: { id: string }[]
): QuoteResponseDraft {
  const singleAvailableFacilityId =
    facilities.length === 1 ? facilities[0].id : null;
  return {
    currency: seed.currency ?? Currency.Usd,
    leadTimeDays: seed.leadTimeDays != null ? String(seed.leadTimeDays) : '',
    sellingOrganizationFacilityId:
      seed.sellingOrganizationFacilityId ?? singleAvailableFacilityId ?? '',
    wholeRequestPickupPrice:
      seed.wholeRequestPickupPrice?.amount.toString() ?? '',
    wholeRequestDeliveryPrice:
      seed.wholeRequestDeliveryPrice?.amount.toString() ?? '',
    rows: buildRowDrafts(seed.groups, seed.items),
  };
}

/**
 * A string that changes only when the form genuinely needs re-seeding: a row appeared or
 * disappeared, or a row acquired its saved quote item id.
 *
 * Deliberately ignores saved prices and quantities. The seller's in-progress values win
 * over the server's, so a change to those must not disturb the form.
 *
 * @example
 * const resetKey = quoteResponseResetKey(quote.id, groups, items);
 */
export function quoteResponseResetKey(
  quoteId: string,
  groups: ProductGroup[],
  items: Map<string, ExistingQuoteItem>
): string {
  const rowKeys = groups.flatMap((group) =>
    group.rows.map(
      (row) =>
        `${row.platformProductLengthId}:${items.get(row.platformProductLengthId)?.id ?? ''}`
    )
  );
  return [quoteId, ...rowKeys].join('|');
}

export function mergeRowDrafts(
  current: QuoteResponseDraft,
  seeded: QuoteResponseDraft
): QuoteResponseDraft {
  const rows = new Map<string, RowDraft>();
  for (const [platformProductLengthId, seededRow] of seeded.rows) {
    const currentRow = current.rows.get(platformProductLengthId);
    rows.set(
      platformProductLengthId,
      currentRow
        ? { ...currentRow, quoteItemId: seededRow.quoteItemId }
        : seededRow
    );
  }
  for (const [platformProductLengthId, currentRow] of current.rows) {
    if (
      currentRow.added &&
      currentRow.quoteItemId == null &&
      !rows.has(platformProductLengthId)
    ) {
      rows.set(platformProductLengthId, currentRow);
    }
  }
  return { ...current, rows };
}

/**
 * Holds the seller's in-progress answer to one quote.
 *
 * @param seed the values to build the draft from
 * @param resetKey from {@link quoteResponseResetKey}; re-seeds when it changes
 * @param usesWholeRequestPrice the buyer's pricing mode for this request
 * @param facilities the seller's organization facilities available to ship from
 *
 * @example
 * const { draft, patch, patchRow, errorKey } = useQuoteResponseDraft(seed, resetKey, usesWholeRequestPrice, facilities);
 */
export function useQuoteResponseDraft(
  seed: QuoteResponseSeed,
  resetKey: string,
  usesWholeRequestPrice: boolean,
  facilities: { id: string }[]
) {
  const [draft, setDraft] = useState<QuoteResponseDraft>(() =>
    seedQuoteResponseDraft(seed, facilities)
  );
  const [seededForResetKey, setSeededForResetKey] = useState(resetKey);

  // Merges during render rather than replacing, and rather than in an effect. The seller's
  // typed values outlive a refetch; only the saved quote item id is adopted, so a resubmit
  // updates the stored item instead of inserting a duplicate.
  if (seededForResetKey !== resetKey) {
    setSeededForResetKey(resetKey);
    setDraft((current) =>
      mergeRowDrafts(current, seedQuoteResponseDraft(seed, facilities))
    );
  }

  const patch = (changes: Partial<Omit<QuoteResponseDraft, 'rows'>>) =>
    setDraft((current) => ({ ...current, ...changes }));

  const patchRow = (
    platformProductLengthId: string,
    changes: Partial<RowDraft>
  ) =>
    setDraft((current) =>
      withPatchedRow(current, platformProductLengthId, changes)
    );

  const addRow = (platformProductLengthId: string, piecesPerBundle: number) =>
    setDraft((current) => {
      if (current.rows.has(platformProductLengthId)) return current;
      const rows = new Map(current.rows);
      rows.set(platformProductLengthId, {
        quoteItemId: null,
        quantityInBundles: '0',
        piecesPerBundle,
        pickupPrice: '',
        deliveryPrice: '',
        added: true,
      });
      return { ...current, rows };
    });

  const withdrawRow = (platformProductLengthId: string) =>
    setDraft((current) => {
      const existing = current.rows.get(platformProductLengthId);
      if (!existing) return current;
      const rows = new Map(current.rows);
      rows.set(platformProductLengthId, {
        ...existing,
        quantityInBundles: '0',
        pickupPrice: '',
        deliveryPrice: '',
      });
      return { ...current, rows };
    });

  const dropRow = (platformProductLengthId: string) =>
    setDraft((current) => {
      if (!current.rows.has(platformProductLengthId)) return current;
      const rows = new Map(current.rows);
      rows.delete(platformProductLengthId);
      return { ...current, rows };
    });

  return {
    draft,
    patch,
    patchRow,
    addRow,
    withdrawRow,
    dropRow,
    errorKey: submissionErrorKey(draft, usesWholeRequestPrice),
    itemOverrides: toItemOverrides(draft, usesWholeRequestPrice),
  };
}
